import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { customerSelector } from '@/api/v1';
import DepositIcon from '@/assets/icons/deposit-icon.svg?react';
import TransferIcon from '@/assets/icons/send-icon.svg?react';
import WithdrawIcon from '@/assets/icons/withdraw-icon.svg?react';
import { DisableInProduction } from '@/components/atoms/DisableInProduction';
import { HomeNavCard } from '@/components/molecules/HomeNavCard';
import { BalanceLayout } from '@/components/organisms/BalanceLayout';
import { MainScreen } from '@/components/organisms/MainScreen';
import { QuoteWidget } from '@/components/organisms/QuoteWidget';
import { useBalance } from '@/hooks/useBalance';
import { usePermissionsManager } from '@/hooks/usePermissionsManager';
import { useTranslator } from '@/hooks/useTranslator';
import { VerifyBusinessBanner } from '@/Screens/Dashboard/Home/VerifyBusinessBanner';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { Customer } from '@/types/customer';
import { PaymentFlowType } from '@/types/paymentFlowType';
import { Permission } from '@/types/permissions';

export const Home: React.FC = (): React.ReactElement => {
  const tr = useTranslator();

  const { data: customer = Customer.empty() } = useSelector(customerSelector);
  const { userIsAuthorizedTo } = usePermissionsManager();

  const usdcBalance = useBalance(CurrencyCodeType.USDC);
  const usdtBalance = useBalance(CurrencyCodeType.USDT);
  const balances = React.useMemo(
    (): [DetailedBalance, DetailedBalance] => [usdcBalance, usdtBalance],
    [usdcBalance, usdtBalance],
  );

  const screenTitle = React.useMemo((): string => {
    if (customer.firstName === '') {
      return tr('Welcome back');
    } else {
      return tr('WELCOME_TITLE', customer.firstName);
    }
  }, [customer.firstName, tr]);

  const shouldVerify = React.useMemo(
    (): boolean => customer.userId !== '' && !customer.kycVerified,
    [customer.kycVerified, customer.userId],
  );

  return (
    <MainScreen title={screenTitle}>
      {shouldVerify && <VerifyBusinessBanner />}

      <Box display="flex" flex={1} minHeight={0} width="100%" height="auto" gap={3} pt="22px">
        <Box sx={styles.balanceWidget}>
          <Paper sx={styles.balancePaper} elevation={1}>
            <BalanceLayout balances={balances} />
          </Paper>
          {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
            <Box sx={styles.cards}>
              <HomeNavCard
                title={tr('TRANSFER')}
                link={PaymentFlowType.transfer}
                icon={TransferIcon}
              />
              <HomeNavCard
                title={tr('DEPOSIT')}
                link={PaymentFlowType.deposit}
                icon={DepositIcon}
              />
              <HomeNavCard
                title={tr('WITHDRAW')}
                link={PaymentFlowType.withdraw}
                icon={WithdrawIcon}
              />
            </Box>
          )}
        </Box>
        {userIsAuthorizedTo(Permission.CREATE_TRANSACTIONS) && (
          <DisableInProduction>
            <Paper sx={[styles.widget, styles.quoteWidget]} elevation={1}>
              <QuoteWidget />
            </Paper>
          </DisableInProduction>
        )}
      </Box>

      <Outlet />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  cards: {
    display: 'flex',
    mt: 2,
    gap: 3,
    alignItems: 'center',
    width: '100%',
  },
  balanceWidget: {
    flexBasis: 650,
    flexShrink: 0,
  },
  balancePaper: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'background.paper',
  },
  quoteWidget: {
    flexBasis: 350,
    flexShrink: 0,
    maxHeight: 'auto',
    display: {
      sm: 'none',
      md: 'block',
    },
  },
  widget: {
    height: 'min-content',
    backgroundColor: 'background.paper',
  },
};
